import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { NavigationService } from '../../../api/services/navigationService';
import { showNotification } from '../../../store/Reducers/NotificationSnackbarSlice';
import { INavigationContent, IRootState } from '../../../types/store';
import {
  IBatchAddServiceMethodResponse,
  INavigationPermissionResponse,
  INavigationServiceMethodResponse,
} from '../../../types/api/response/navigationresponse';

interface Props {
  navigation: INavigationContent | null;
  isOpen: boolean;
  onClose: () => void;
}

export const ViewNavigationDialog = ({ navigation, isOpen, onClose }: Props) => {
  const [tab, setTab] = useState(0);

  useEffect(() => {
    if (isOpen) setTab(0);
  }, [isOpen]);

  if (!navigation) return null;

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      slotProps={{
        paper: {
          sx: { width: { xs: '95%', sm: 640 }, maxHeight: '80vh' },
        },
      }}
    >
      <DialogTitle
        sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}
      >
        <Typography fontWeight={600}>{navigation.title}</Typography>
        <IconButton size="small" onClick={onClose} sx={{ color: 'grey.700' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{ px: 3, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label="Details" />
        <Tab label="Permissions" />
        <Tab label="Service Methods" />
      </Tabs>

      <DialogContent sx={{ pt: 2 }}>
        {tab === 0 && <DetailsTab navigation={navigation} />}
        {tab === 1 && <PermissionsTab navigation={navigation} />}
        {tab === 2 && <ServiceMethodsTab navigation={navigation} />}
      </DialogContent>
    </Dialog>
  );
};

// ─── Details Tab ─────────────────────────────────────────────────────────────

const DetailsTab = ({ navigation }: { navigation: INavigationContent }) => {
  const { content: navigations } = useSelector((state: IRootState) => state.navigations);
  const parent = navigation.parent_id
    ? navigations.find((n) => n.id === navigation.parent_id)
    : null;

  const rows = [
    { label: 'Title', value: navigation.title },
    { label: 'Icon', value: navigation.icon || '—' },
    { label: 'Route', value: navigation.route },
    { label: 'Description', value: navigation.description },
    { label: 'Display Order', value: String(navigation.display_order) },
    { label: 'Active', value: navigation.is_active ? 'Yes' : 'No' },
    { label: 'Parent', value: parent?.title ?? '—' },
    {
      label: 'Created At',
      value: navigation.created_at
        ? moment(navigation.created_at).format('MMMM Do YYYY, h:mm:ss a')
        : '—',
    },
    {
      label: 'Updated At',
      value: navigation.updated_at
        ? moment(navigation.updated_at).format('MMMM Do YYYY, h:mm:ss a')
        : '—',
    },
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {rows.map(({ label, value }) => (
        <Box key={label}>
          <Typography variant="caption" color="text.secondary" fontWeight={600}>
            {label}
          </Typography>
          <Typography variant="body2">{value}</Typography>
          <Divider sx={{ mt: 1 }} />
        </Box>
      ))}
      {navigation.children?.length > 0 && (
        <Box>
          <Typography variant="caption" color="text.secondary" fontWeight={600}>
            Subpages
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
            {navigation.children.map((c) => (
              <Chip key={c.id} label={`${c.title} (${c.route})`} size="small" />
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};

// ─── Permissions Tab ──────────────────────────────────────────────────────────

const PermissionsTab = ({ navigation }: { navigation: INavigationContent }) => {
  const dispatch = useDispatch();
  const { content: allPermissions } = useSelector((state: IRootState) => state.permissions);

  const [permissions, setPermissions] = useState<INavigationPermissionResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<number[]>([]);
  const [assigning, setAssigning] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const data = await NavigationService.getNavigationPermissions(navigation.id);
      setPermissions(data);
    } catch (e: any) {
      dispatch(
        showNotification({
          message: `Failed to load permissions. ${e?.response?.data?.message || ''}`,
          severity: 'error',
        }),
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [navigation.id]);

  const handleAssign = async () => {
    if (!selected.length) return;
    setAssigning(true);
    try {
      await NavigationService.assignPermissions(navigation.id, { permission_ids: selected });
      dispatch(showNotification({ message: 'Permissions assigned.' }));
      setSelected([]);
      load();
    } catch (e: any) {
      dispatch(
        showNotification({
          message: `Failed to assign permissions. ${e?.response?.data?.message || ''}`,
          severity: 'error',
        }),
      );
    } finally {
      setAssigning(false);
    }
  };

  const handleRemove = async (permissionId: number) => {
    try {
      await NavigationService.removePermission(navigation.id, permissionId);
      dispatch(showNotification({ message: 'Permission removed.' }));
      setPermissions((prev) => prev.filter((p) => p.id !== permissionId));
    } catch (e: any) {
      dispatch(
        showNotification({
          message: `Failed to remove permission. ${e?.response?.data?.message || ''}`,
          severity: 'error',
        }),
      );
    }
  };

  const assignablePermissions =
    allPermissions?.filter((p) => !permissions.some((existing) => existing.id === p.id)) ?? [];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {loading ? (
        <Box display="flex" justifyContent="center">
          <CircularProgress size={24} />
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {permissions.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              No permissions assigned.
            </Typography>
          )}
          {permissions.map((p) => (
            <Chip key={p.id} label={p.name} size="small" onDelete={() => handleRemove(p.id)} />
          ))}
        </Box>
      )}
      <Divider />
      <Typography variant="caption" fontWeight={600} color="text.secondary">
        Assign Permissions
      </Typography>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
        <Autocomplete
          multiple
          sx={{ flex: 1 }}
          options={assignablePermissions}
          disableCloseOnSelect
          getOptionLabel={(option) => option.name}
          value={assignablePermissions.filter((p) => selected.includes(p.id))}
          onChange={(_, opts) => setSelected(opts.map((o) => o.id))}
          renderOption={(props, option, { selected: isSelected }) => (
            <li {...props}>
              <Checkbox style={{ marginRight: 8 }} checked={isSelected} />
              {option.name}
            </li>
          )}
          renderInput={(params) => (
            <TextField {...params} size="small" placeholder="Select permissions" />
          )}
        />
        <Button
          variant="contained"
          size="small"
          disabled={!selected.length || assigning}
          onClick={handleAssign}
          sx={{ backgroundColor: '#000', flexShrink: 0, height: 40 }}
        >
          {assigning ? 'Assigning...' : 'Assign'}
        </Button>
      </Box>
    </Box>
  );
};

// ─── Service Methods Tab ──────────────────────────────────────────────────────

const ServiceMethodsTab = ({ navigation }: { navigation: INavigationContent }) => {
  const dispatch = useDispatch();
  const [serviceMethods, setServiceMethods] = useState<INavigationServiceMethodResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [batchInput, setBatchInput] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const data = await NavigationService.getServiceMethods(navigation.id);
      setServiceMethods(data);
    } catch (e: any) {
      dispatch(
        showNotification({
          message: `Failed to load service methods. ${e?.response?.data?.message || ''}`,
          severity: 'error',
        }),
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [navigation.id]);

  const handleAdd = async () => {
    const lines = batchInput
      .split('\n')
      .map((s) => s.trim())
      .filter(Boolean);
    if (!lines.length) return;

    setSubmitting(true);
    try {
      let result: IBatchAddServiceMethodResponse | INavigationServiceMethodResponse;
      if (lines.length === 1) {
        result = await NavigationService.addServiceMethod(navigation.id, {
          service_method: lines[0],
        });
        dispatch(showNotification({ message: 'Service method added.' }));
      } else {
        result = (await NavigationService.batchAddServiceMethods(navigation.id, {
          service_methods: lines,
        })) as IBatchAddServiceMethodResponse;
        const { created, skipped } = result as IBatchAddServiceMethodResponse;
        dispatch(
          showNotification({ message: `Added ${created}, skipped ${skipped} (already mapped).` }),
        );
      }
      setBatchInput('');
      load();
    } catch (e: any) {
      dispatch(
        showNotification({
          message: `Failed to add service method(s). ${e?.response?.data?.message || ''}`,
          severity: 'error',
        }),
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemove = async (id: number) => {
    try {
      await NavigationService.removeServiceMethod(navigation.id, id);
      dispatch(showNotification({ message: 'Service method removed.' }));
      setServiceMethods((prev) => prev.filter((sm) => sm.id !== id));
    } catch (e: any) {
      dispatch(
        showNotification({
          message: `Failed to remove service method. ${e?.response?.data?.message || ''}`,
          severity: 'error',
        }),
      );
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {loading ? (
        <Box display="flex" justifyContent="center">
          <CircularProgress size={24} />
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {serviceMethods.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              No service methods mapped.
            </Typography>
          )}
          {serviceMethods.map((sm) => (
            <Chip
              key={sm.id}
              label={sm.service_method}
              size="small"
              onDelete={() => handleRemove(sm.id)}
            />
          ))}
        </Box>
      )}
      <Divider />
      <Typography variant="caption" fontWeight={600} color="text.secondary">
        Add Service Methods (one per line for batch)
      </Typography>
      <TextField
        multiline
        minRows={3}
        value={batchInput}
        onChange={(e) => setBatchInput(e.target.value)}
        placeholder={
          'e.g.\ncom.app.controller.ProductController.getAll\ncom.app.controller.OrderController.*'
        }
        fullWidth
        size="small"
      />
      <Button
        variant="contained"
        size="small"
        disabled={!batchInput.trim() || submitting}
        onClick={handleAdd}
        sx={{ backgroundColor: '#000', alignSelf: 'flex-end' }}
      >
        {submitting ? 'Adding...' : 'Add'}
      </Button>
    </Box>
  );
};
